import { useEffect } from "react";

interface SEOProps {
  title: string;
  description: string;
  path?: string;
  noindex?: boolean;
  ogImage?: string;
  jsonLd?: object | object[];
}

const BASE_URL = "https://www.aiforhrmastermind.com";

const setMeta = (selector: string, attr: string, value: string) => {
  const el = document.querySelector(selector);
  if (el) el.setAttribute(attr, value);
};

export const useSEO = ({ title, description, path = "/", noindex = false, ogImage, jsonLd }: SEOProps) => {
  const jsonLdKey = JSON.stringify(jsonLd ?? null);

  useEffect(() => {
    const fullTitle = title.includes("AI for HR")
      ? title
      : `${title} | AI for HR Mastermind`;
    const url = `${BASE_URL}${path}`;

    document.title = fullTitle;

    setMeta('meta[name="description"]', "content", description);
    setMeta('meta[property="og:title"]', "content", fullTitle);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[property="og:url"]', "content", url);
    setMeta('link[rel="canonical"]', "href", url);
    setMeta('meta[name="twitter:title"]', "content", fullTitle);
    setMeta('meta[name="twitter:description"]', "content", description);

    if (ogImage) {
      const imgUrl = ogImage.startsWith("http") ? ogImage : `${BASE_URL}${ogImage}`;
      setMeta('meta[property="og:image"]', "content", imgUrl);
      setMeta('meta[name="twitter:image"]', "content", imgUrl);
    }

    // robots noindex (per route)
    let robotsTag = document.querySelector('meta[name="robots"]');
    if (noindex) {
      if (!robotsTag) {
        robotsTag = document.createElement("meta");
        robotsTag.setAttribute("name", "robots");
        document.head.appendChild(robotsTag);
      }
      robotsTag.setAttribute("content", "noindex, nofollow");
    } else if (robotsTag) {
      robotsTag.parentNode?.removeChild(robotsTag);
    }

    // route-scoped JSON-LD
    const scripts: HTMLScriptElement[] = [];
    const parsedJsonLd = jsonLdKey === "null" ? null : JSON.parse(jsonLdKey);
    if (parsedJsonLd) {
      const items = Array.isArray(parsedJsonLd) ? parsedJsonLd : [parsedJsonLd];
      items.forEach((item) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.dataset.seo = "route";
        script.textContent = JSON.stringify(item);
        document.head.appendChild(script);
        scripts.push(script);
      });
    }

    return () => {
      scripts.forEach((s) => s.parentNode?.removeChild(s));
      const stale = document.querySelector('meta[name="robots"]');
      if (stale && noindex) stale.parentNode?.removeChild(stale);
    };
  }, [title, description, path, noindex, ogImage, jsonLdKey]);
};
